import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { COMPANIES, IMAGES } from '../data/content';
import { SectionHeader } from '../components/SectionHeader';
import { GROUP } from '../constants/testIds';

export default function GroupCompanies() {
  return (
    <div className="bg-white">
      <section className="relative pt-44 pb-20 bg-[#0D1B2A] grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${IMAGES.epc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-[#0D1B2A]/82" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="overline text-white/80 flex items-center gap-3 mb-6">
            <span className="gold-rule" /> Group Companies
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            Six specialist businesses. <span className="text-[#D4AF37]">One integrated platform.</span>
          </h1>
          <p className="mt-8 text-white/75 text-lg max-w-3xl leading-relaxed">
            Each Clan Group company leads its discipline — operating with a shared standard of
            engineering, execution and accountability.
          </p>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 space-y-24 md:space-y-32">
          {COMPANIES.map((c, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={c.slug}
                id={c.slug}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-32"
              >
                <motion.div
                  initial={{ opacity: 0, x: reverse ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7 }}
                  className={`lg:col-span-7 ${reverse ? 'lg:order-2' : ''}`}
                >
                  <div className="relative">
                    <img
                      src={c.image}
                      alt={c.name}
                      className="w-full h-[420px] md:h-[520px] object-cover"
                    />
                    <div
                      className="absolute -bottom-5 -left-5 h-20 w-20"
                      style={{ background: c.accent }}
                    />
                    <div
                      className="absolute top-0 left-0 h-1.5 w-32"
                      style={{ background: c.accent }}
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}
                >
                  <div className="overline mb-4 flex items-center gap-3">
                    <span className="gold-rule" /> Group Company {String(i + 1).padStart(2, '0')}
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0D1B2A] leading-[1.05]">
                    {c.name}
                  </h2>
                  <div className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#4B5563]">
                    {c.discipline}
                  </div>
                  {c.tag && (
                    <div className="mt-2 text-sm text-[#2A9D8F] font-semibold">{c.tag}</div>
                  )}
                  <p className="mt-6 text-[#4B5563] text-[15px] leading-relaxed">{c.summary}</p>
                  <ul className="mt-7 grid sm:grid-cols-2 gap-2.5">
                    {c.capabilities.map((cap) => (
                      <li
                        key={cap}
                        className="flex items-center gap-2.5 text-[14px] text-[#0D1B2A]"
                      >
                        <Check size={15} className="text-[#D4AF37] shrink-0" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-9">
                    {c.external ? (
                      <a
                        href={c.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={GROUP.cardCta(c.slug)}
                        className="btn-ghost-dark"
                      >
                        {c.cta || 'Visit Website'} <ExternalLink size={15} />
                      </a>
                    ) : (
                      <Link
                        to="/contact"
                        data-testid={GROUP.cardCta(c.slug)}
                        className="btn-ghost-dark"
                      >
                        Discuss a Project <ArrowRight size={15} />
                      </Link>
                    )}
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Weldmatix systems showcase */}
      <section className="bg-[#0D1B2A] py-24 md:py-28 grain">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader
            overline="Weldmatix Fusion · Systems"
            title="Welding automation. Precision engineered."
            lead="A glimpse into Weldmatix Fusion's product systems — explore the complete catalogue at weldmatixco.com."
            light
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: 'Continuous Rotating Torch', img: IMAGES.weldmatixCRT },
              { name: 'Vertical Clad Station', img: IMAGES.weldmatixVCS },
              { name: 'Pipe Cladding Rig', img: IMAGES.weldmatixSystem },
              { name: 'OMNI Clad Station', img: IMAGES.weldmatixOCS },
            ].map((s, i) => (
              <motion.a
                key={s.name}
                href="https://weldmatixco.com"
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group block bg-[#132338] border border-white/8 overflow-hidden"
              >
                <div className="h-48 overflow-hidden bg-white">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#D4AF37]">
                      System
                    </div>
                    <div className="font-display font-bold text-white text-base mt-1">
                      {s.name}
                    </div>
                  </div>
                  <ExternalLink size={16} className="text-white/60 group-hover:text-[#D4AF37] transition-colors" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
